import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import styled from '@emotion/styled';

import { SubscriptionPlan, SubscriberType, subscriptionActions } from '../../../redux/slices/subscriptionSlice';
import { useAppDisPatch, useAppSelector } from '../../../redux/store';
import { DASHBOARD, SUBSCRIPTIONS_CONFIRMATION } from '../../../routes/routeConstants';
import useGetPlans from '../../../hooks/dashboard/subscription/useGetPlans';
import useGetMySubscription from '../../../hooks/dashboard/subscription/useGetMySubscription';
import useInitializePayment from '../../../hooks/dashboard/subscription/useInitializePayment';
import PaystackCheckoutLauncher from '../../../components/Subscription/PaystackCheckoutLauncher';
import PlanCard from '../../../components/Subscription/PlanCard';

type Audience = 'LOCUM' | 'RECRUITER' | 'INSTITUTION';
type Interval = 'MONTHLY' | 'ANNUAL';
const institutionFormUrl = 'https://form.jotform.com/253536502796565';

const managedFeatures = [
  'We manage your vacancies for you',
  'Access to unlimited applicants',
  'Get one month access to list unlimited vacancies',
  'Promoted Job Posts',
  'Regular updates on Job Posts',
];

const institutionPlans = {
  MONTHLY: [
    { name: 'Recruit4Me', amount: 10000, featured: true, features: managedFeatures },
    { name: 'Recruit4Me Lite', amount: 2000, featured: false, features: ['One Job post', 'Promoted Job post', 'Regular updates on Job posts'] },
  ],
  ANNUAL: [
    { name: 'Recruit4Me', amount: 100000, featured: true, features: managedFeatures },
  ],
};

const SubscriptionPlansPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDisPatch();
  const { authData } = useAppSelector((state) => state.auth);
  const { plans, activeSubscription, checkoutSession, error } = useAppSelector((state) => state.subscription);
  const { getPlans, loading: plansLoading } = useGetPlans();
  useGetMySubscription();
  const { initializePayment, loading: paymentLoading } = useInitializePayment();
  const [audience, setAudience] = useState<Audience>(authData?.companyId ? 'RECRUITER' : 'LOCUM');
  const [interval, setBillingInterval] = useState<Interval>('MONTHLY');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const paymentCloseRef = useRef<HTMLButtonElement>(null);
  const pricingDialogRef = useRef<HTMLElement>(null);
  const paymentDialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    getPlans();
  }, [getPlans]);

  useEffect(() => {
    if (authData?.companyId) setAudience('RECRUITER');
  }, [authData?.companyId]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (selectedPlan) paymentCloseRef.current?.focus();
    else closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        const dialog = selectedPlan ? paymentDialogRef.current : pricingDialogRef.current;
        const focusable = dialog?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]');
        if (!focusable?.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      if (event.key !== 'Escape' || checkoutSession) return;
      if (selectedPlan) setSelectedPlan(null);
      else navigate(`/${DASHBOARD}`);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [checkoutSession, navigate, selectedPlan]);

  const subscriberType: SubscriberType = audience === 'LOCUM' ? 'LOCUM' : 'COMPANY';
  const visiblePlans = plans.filter((plan) => plan.isActive &&
    (plan.subscriberType === subscriberType || plan.code === 'FREE') &&
    (plan.code === 'FREE' || plan.billingInterval === interval));
  const orderedPlans = [...visiblePlans].sort((first, second) => {
    const rank = (plan: SubscriptionPlan) => plan.code === 'FREE' ? 0 : plan.code.endsWith('PLUS') ? 1 : 2;
    return rank(first) - rank(second);
  });

  const choosePlan = (plan: SubscriptionPlan) => {
    if (plan.code === 'FREE' || plan.id === activeSubscription?.plan.id) return;
    dispatch(subscriptionActions.setError(null));
    setSelectedPlan(plan);
  };

  const close = () => navigate(`/${DASHBOARD}`);
  const handleCheckoutSuccess = useCallback(() => {
    if (checkoutSession?.reference) {
      navigate(`/${SUBSCRIPTIONS_CONFIRMATION}?reference=${encodeURIComponent(checkoutSession.reference)}`);
    }
  }, [checkoutSession?.reference, navigate]);
  const handleCheckoutClose = useCallback(() => {
    setSelectedPlan(null);
    dispatch(subscriptionActions.setCheckoutSession(null));
  }, [dispatch]);

  return createPortal(
    <Backdrop onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
      <PricingDialog ref={pricingDialogRef} role="dialog" aria-modal="true" aria-label="Choose a subscription plan">
        <CloseButton ref={closeButtonRef} type="button" aria-label="Close pricing" onClick={close}>×</CloseButton>
        <DialogControls>
          <IntervalSwitch aria-label="Billing period">
            <SwitchButton type="button" active={interval === 'MONTHLY'} aria-pressed={interval === 'MONTHLY'} onClick={() => setBillingInterval('MONTHLY')}>Monthly</SwitchButton>
            <SwitchButton type="button" active={interval === 'ANNUAL'} aria-pressed={interval === 'ANNUAL'} onClick={() => setBillingInterval('ANNUAL')}>Yearly</SwitchButton>
          </IntervalSwitch>
          <AudienceTabs aria-label="Plan category">
            <AudienceButton type="button" active={audience === 'LOCUM'} aria-pressed={audience === 'LOCUM'} onClick={() => setAudience('LOCUM')}>Locum</AudienceButton>
            <AudienceButton type="button" active={audience === 'RECRUITER'} aria-pressed={audience === 'RECRUITER'} onClick={() => setAudience('RECRUITER')}>Recruiter</AudienceButton>
            <AudienceButton type="button" active={audience === 'INSTITUTION'} aria-pressed={audience === 'INSTITUTION'} onClick={() => setAudience('INSTITUTION')}>Institution</AudienceButton>
          </AudienceTabs>
        </DialogControls>
        {audience === 'INSTITUTION' ? (
          <InstitutionSection>
            <InstitutionGrid>
              {institutionPlans[interval].map((plan) => (
                <InstitutionCard key={plan.name} featured={plan.featured}>
                  <InstitutionName>{plan.name}</InstitutionName>
                  <InstitutionPrice>NGN{plan.amount.toLocaleString('en-NG')}</InstitutionPrice>
                  <InstitutionPeriod>Per {interval === 'ANNUAL' ? 'Year' : 'Month'}</InstitutionPeriod>
                  <InstitutionDivider />
                  <InstitutionIncluded>What's included?</InstitutionIncluded>
                  <InstitutionFeatures>
                    {plan.features.map((feature) => (
                      <li key={feature}><InstitutionCheck featured={plan.featured} aria-hidden="true">✓</InstitutionCheck><span>{feature}</span></li>
                    ))}
                  </InstitutionFeatures>
                  <InstitutionAction href={institutionFormUrl} target="_blank" rel="noopener noreferrer" featured={plan.featured}>
                    Get Started
                  </InstitutionAction>
                </InstitutionCard>
              ))}
            </InstitutionGrid>
            <InstitutionNote>Institution plans are managed by the Locumspace team. Get Started opens the interest form in a new tab.</InstitutionNote>
          </InstitutionSection>
        ) : plansLoading && !plans.length ? (
          <EmptyMessage>Loading plans…</EmptyMessage>
        ) : orderedPlans.length ? (
          <CardsGrid>
            {orderedPlans.map((plan) => <PlanCard key={plan.id} plan={plan} isCurrentPlan={activeSubscription?.plan.id === plan.id} onSelect={choosePlan} />)}
          </CardsGrid>
        ) : (
          <EmptyMessage>{error || `No ${interval.toLowerCase()} ${audience.toLowerCase()} plans are available yet.`}</EmptyMessage>
        )}
      </PricingDialog>

      {selectedPlan && !checkoutSession && (
        <PaymentBackdrop onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedPlan(null); }}>
          <PaymentDialog ref={paymentDialogRef} role="dialog" aria-modal="true" aria-label="Payment summary">
            <PaymentHeader>
              <h2>Payment</h2>
              <RoundClose ref={paymentCloseRef} type="button" aria-label="Close payment summary" onClick={() => setSelectedPlan(null)}>×</RoundClose>
            </PaymentHeader>
            <PaymentSummary>
              <strong>{selectedPlan.name}</strong>
              <strong>{selectedPlan.currency}{Number(selectedPlan.amount).toLocaleString('en-NG', { maximumFractionDigits: 0 })}/{selectedPlan.billingInterval === 'ANNUAL' ? 'year' : 'month'}</strong>
            </PaymentSummary>
            <h3>Payment method</h3>
            <PaymentOption>
              <span>Pay securely with Paystack<small>Available card, bank and transfer methods appear at checkout.</small></span>
              <RadioMark aria-hidden="true" />
            </PaymentOption>
            {error && <PaymentError role="alert">{error}</PaymentError>}
            <PaymentActions>
              <NextButton type="button" disabled={paymentLoading} onClick={() => {
                dispatch(subscriptionActions.setError(null));
                initializePayment(selectedPlan.id);
              }}>{paymentLoading ? 'Please wait…' : 'Next'}</NextButton>
            </PaymentActions>
          </PaymentDialog>
        </PaymentBackdrop>
      )}

      <PaystackCheckoutLauncher
        session={checkoutSession}
        onSuccess={handleCheckoutSuccess}
        onClose={handleCheckoutClose}
      />
    </Backdrop>, document.body
  );
};

export default SubscriptionPlansPage;

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center;
  padding: 24px; background: rgba(17, 24, 39, 0.48);
`;
const PricingDialog = styled.section`
  position: relative; width: min(1690px, 100%); max-height: calc(100vh - 48px); overflow: auto;
  border-radius: 12px; background: #fff; padding: clamp(28px, 4vw, 72px);
  box-shadow: 0 24px 80px rgba(17, 24, 39, 0.16);
`;
const CloseButton = styled.button`
  position: absolute; top: 48px; right: 48px; border: 0; background: #243044; color: white;
  border-radius: 50%; width: 28px; height: 28px; font-size: 22px; line-height: 24px; cursor: pointer;
  &:focus-visible { outline: 3px solid #2855c6; outline-offset: 3px; }
`;
const DialogControls = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 24px; margin: 95px 0 30px;
  @media (max-width: 700px) { flex-direction: column; align-items: stretch; margin-top: 55px; }
`;
const IntervalSwitch = styled.div`
  display: flex; padding: 5px; border-radius: 999px; background: #f5f5f7; min-width: 290px;
`;
const SwitchButton = styled.button<{ active: boolean }>`
  flex: 1; padding: 12px 24px; border: 0; border-radius: 999px;
  background: ${({ active }) => active ? '#2855c6' : 'transparent'};
  color: ${({ active }) => active ? '#fff' : '#50545b'};
  font-size: 16px; font-weight: 600; cursor: pointer;
`;
const AudienceTabs = styled.div`
  display: flex; align-items: center; gap: 24px; flex-wrap: wrap;
`;
const AudienceButton = styled.button<{ active: boolean }>`
  border: 0; background: transparent; color: ${({ active }) => active ? '#23499e' : '#595d64'};
  font-size: 16px; font-weight: 700; cursor: pointer; padding: 8px 0;
`;
const CardsGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: clamp(16px, 2.2vw, 42px);
  @media (max-width: 1050px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const EmptyMessage = styled.p`
  padding: 70px 20px; text-align: center; color: #5a6170;
`;
const InstitutionSection = styled.div`
  width: 100%;
`;
const InstitutionGrid = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 490px)); gap: clamp(16px, 2.2vw, 42px);
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const InstitutionCard = styled.article<{ featured: boolean }>`
  display: flex; flex-direction: column; min-height: 660px; padding: clamp(24px, 2vw, 38px);
  border: 1px solid #2855c6; border-radius: 12px;
  background: ${({ featured }) => featured ? '#284b9f' : '#fff'};
  color: ${({ featured }) => featured ? '#fff' : '#15171c'};
  @media (max-width: 700px) { min-height: 560px; }
`;
const InstitutionName = styled.h2`
  margin: 0 0 12px; font-size: clamp(20px, 1.5vw, 24px); text-transform: uppercase;
`;
const InstitutionPrice = styled.strong`
  font-size: clamp(32px, 2.8vw, 50px); line-height: 1.1;
`;
const InstitutionPeriod = styled.span`
  margin-top: 8px; font-size: 17px;
`;
const InstitutionDivider = styled.hr`
  width: 100%; border: 0; border-top: 1px solid currentColor; margin: 28px 0; opacity: 0.65;
`;
const InstitutionIncluded = styled.h3`
  margin: 0 0 20px; font-size: 17px;
`;
const InstitutionFeatures = styled.ul`
  display: flex; flex-direction: column; gap: 20px; flex: 1; margin: 0 0 40px; padding: 0; list-style: none;
  li { display: flex; align-items: flex-start; gap: 13px; font-size: 17px; line-height: 1.4; }
`;
const InstitutionCheck = styled.span<{ featured: boolean }>`
  display: inline-grid; place-items: center; width: 23px; height: 23px; flex: none;
  border-radius: 50%; background: ${({ featured }) => featured ? '#fff' : '#243044'};
  color: ${({ featured }) => featured ? '#284b9f' : '#fff'}; font-size: 15px;
`;
const InstitutionAction = styled.a<{ featured: boolean }>`
  display: block; width: 100%; border: 1px solid #2855c6; border-radius: 999px; padding: 14px 20px;
  background: ${({ featured }) => featured ? '#fff' : 'transparent'};
  color: #284b9f; font-size: 18px; font-weight: 700; text-align: center; text-decoration: none; cursor: pointer;
  &:focus-visible { outline: 3px solid #8657ff; outline-offset: 3px; }
`;
const InstitutionNote = styled.p`
  margin: 18px 0 0; color: #5a6170; font-size: 14px;
`;
const PaymentBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1; display: grid; place-items: center;
  padding: 20px; background: rgba(17, 24, 39, 0.45);
`;
const PaymentDialog = styled.section`
  width: min(950px, 100%); border-radius: 12px; background: #fff; padding: clamp(24px, 4vw, 60px);
  h2 { margin: 0; font-size: 26px; }
  h3 { margin: 44px 0 18px; font-size: 23px; }
`;
const PaymentHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 38px;
`;
const RoundClose = styled.button`
  border: 0; background: #243044; color: white; border-radius: 50%; width: 28px; height: 28px;
  font-size: 22px; line-height: 24px; cursor: pointer;
`;
const PaymentSummary = styled.div`
  display: flex; justify-content: space-between; gap: 20px; padding: 26px; border-radius: 6px;
  background: #f6f6f7; font-size: 22px;
  @media (max-width: 600px) { flex-direction: column; font-size: 18px; }
`;
const PaymentOption = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 16px 0; font-size: 19px;
  small { display: block; margin-top: 6px; font-size: 13px; color: #606979; }
`;
const RadioMark = styled.span`
  width: 24px; height: 24px; border: 6px solid #6746ed; border-radius: 50%; flex: none;
`;
const PaymentActions = styled.div`
  display: flex; justify-content: flex-end; margin-top: 28px;
`;
const PaymentError = styled.p`
  margin: 14px 0 0; color: #b42318; font-size: 14px;
`;
const NextButton = styled.button`
  min-width: 150px; padding: 12px 30px; border: 0; border-radius: 999px;
  background: #2855c6; color: white; font-size: 16px; font-weight: 700; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: wait; }
`;
